import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, ProgressBar, Button, Alert } from 'react-bootstrap';
import { FaceScanModuleData, SkinScanData, UserDemographics } from '../types';
import { KTSVG } from '../../../../_metronic/helpers/components/KTSVG';

interface FaceScanIframeProps {
  userData: UserDemographics;
  scanUid: string;
  onScanComplete: (data: FaceScanModuleData) => void;
  onSkinScanComplete?: (data: SkinScanData) => void;
  onError: (error: string) => void;
  onCancel?: () => void;
}

const FACE_SCAN_BASE_URL = 'https://prod.d2m8xldi52cxh7.amplifyapp.com/';
const DEFAULT_FACE_SCAN_CONFIGS = {
  takeScreenshot: true,
};
const DEFAULT_IFRAME_PARAMS = {
  posture: 'sitting',
  hospitalId: 'futurisk-open',
  hospitalName: 'Futurisk Wellness',
};

const isIOSDevice = () => {
  if (typeof navigator === 'undefined') {
    return false;
  }
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes('Mac') && navigator.maxTouchPoints > 1);
};

const FaceScanIframe: React.FC<FaceScanIframeProps> = ({
  userData,
  scanUid,
  onScanComplete,
  onSkinScanComplete,
  onError,
  onCancel,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const latestOnError = useRef(onError);
  const hasAutoInitializedRef = useRef(false);
  const [progress, setProgress] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [scanStatus, setScanStatus] = useState<string>('Initializing...');
  const [iframeUrl, setIframeUrl] = useState<string>('');
  const [permissionStatus, setPermissionStatus] = useState<'idle' | 'requesting' | 'granted' | 'error'>('idle');
  const [permissionError, setPermissionError] = useState<string | null>(null);
  const requiresGestureForCamera = useMemo(() => isIOSDevice(), []);

  const faceScanUrl = useMemo(() => {
    const params = new URLSearchParams();
    params.set('faceScanConfigs', JSON.stringify(DEFAULT_FACE_SCAN_CONFIGS));
    params.set('uid', scanUid);
    params.set('username', userData.name);
    params.set('hospitalId', DEFAULT_IFRAME_PARAMS.hospitalId);
    params.set('hospitalName', DEFAULT_IFRAME_PARAMS.hospitalName);
    params.set('posture', DEFAULT_IFRAME_PARAMS.posture);
    params.set('gender', userData.gender);
    params.set('phone', userData.phone);
    params.set('dob', userData.dob);
    params.set('age', String(userData.age));
    params.set('weight', String(userData.weight));
    params.set('height', String(userData.height));
    if (userData.email) {
      params.set('email', userData.email);
    }
    return `${FACE_SCAN_BASE_URL}?${params.toString()}`;
  }, [scanUid, userData]);

  useEffect(() => {
    latestOnError.current = onError;
  }, [onError]);

  const initializeCamera = useCallback(async (trigger: 'auto' | 'manual' = 'auto') => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setPermissionError('Camera APIs are not available in this browser.');
      setPermissionStatus('error');
      return;
    }

    if (typeof window !== 'undefined' && window.isSecureContext === false) {
      setPermissionError('Camera access requires HTTPS or running on localhost.');
      setPermissionStatus('error');
      return;
    }

    setPermissionStatus('requesting');
    setPermissionError(null);

    try {
      // Simplified approach: Just load the iframe directly and let it handle camera access
      // The iframe's allow attribute will enable it to request camera permissions
      console.log('Initializing camera access for iframe...');
      setPermissionStatus('granted');
      setScanStatus('Camera ready. Initializing scan...');
      setIsLoading(true);
      
      // Load iframe with timestamp to ensure fresh load with permissions
      setIframeUrl(`${faceScanUrl}&session=${Date.now()}`);
      
    } catch (error: any) {
      console.error('Camera initialization error:', error);
      setPermissionStatus('error');
      setPermissionError('Unable to initialize camera. Please refresh and try again.');
    }
  }, [faceScanUrl]);

  useEffect(() => {
    setIsLoading(true);
  }, [faceScanUrl]);

  useEffect(() => {
    if (!requiresGestureForCamera && !hasAutoInitializedRef.current) {
      hasAutoInitializedRef.current = true;
      // Small delay to ensure component is mounted
      setTimeout(() => {
        initializeCamera('auto');
      }, 100);
    }
  }, [initializeCamera, requiresGestureForCamera]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Note: CORS warnings in console are normal for cross-origin iframes and don't affect functionality
      // The iframe (FaceSense SDK) is loaded from amplifyapp.com but communicates via postMessage
      
      // Verify origin for security - only accept messages from FaceSense SDK
      if (!event.origin.includes('amplifyapp.com')) {
        return;
      }

      try {
        console.log('=== Message received from iframe ===');
        console.log('Event data:', event.data);
        console.log('Event data type:', typeof event.data);
        
        // Parse JSON string if needed
        let messageData = event.data;
        if (typeof messageData === 'string') {
          try {
            messageData = JSON.parse(messageData);
            console.log('✅ Parsed JSON string to object');
          } catch (parseErr) {
            console.log('Message is string but not JSON:', messageData);
            return;
          }
        }

        // Handle direct scan data (FaceSense SDK sends data directly, not wrapped in type)
        if (messageData && typeof messageData === 'object') {
          // Check if it's scan data (has raw_intensity and ppg_time)
          if (messageData.raw_intensity && messageData.ppg_time) {
            console.log('✅ Detected scan completion data');
            console.log('Scan data preview:', {
              raw_intensity_length: messageData.raw_intensity?.length,
              ppg_time_length: messageData.ppg_time?.length,
              average_fps: messageData.average_fps,
              scanType: messageData.scanType
            });
            setScanStatus('Scan completed successfully!');
            setProgress(100);
            onScanComplete(messageData);
            return;
          }

          // Check if it's a screenshot/image data (skin scan)
          if (messageData.image || messageData.screenshot || messageData.base64Image) {
            console.log('✅ Detected screenshot/image data for skin scan');
            if (onSkinScanComplete) {
              const skinData: SkinScanData = {
                image: messageData.image || messageData.screenshot || messageData.base64Image,
                timestamp: messageData.timestamp || new Date().toISOString()
              };
              console.log('Skin data prepared:', { 
                hasImage: !!skinData.image, 
                imageLength: skinData.image?.length 
              });
              onSkinScanComplete(skinData);
            }
            return;
          }

          // Check if it's a typed message
          if (messageData.type) {
            switch (messageData.type) {
              case 'scan_complete':
                console.log('✅ Received scan_complete event');
                if (messageData.payload) {
                  setScanStatus('Scan completed successfully!');
                  setProgress(100);
                  onScanComplete(messageData.payload);
                }
                break;

              case 'screenshot':
              case 'image':
              case 'skinScan':
                console.log('✅ Received screenshot/image event');
                if (messageData.data && onSkinScanComplete) {
                  try {
                    const skinData: SkinScanData = typeof messageData.data === 'string' 
                      ? JSON.parse(messageData.data) 
                      : messageData.data;
                    console.log('Parsed skin data:', { 
                      hasImage: !!skinData.image, 
                      imageLength: skinData.image?.length 
                    });
                    onSkinScanComplete(skinData);
                  } catch (err) {
                    console.error('Error parsing skin scan data:', err);
                  }
                }
                break;

              case 'scan_progress':
                if (messageData.progress !== undefined) {
                  setProgress(messageData.progress);
                  setScanStatus(`Scanning... ${Math.round(messageData.progress)}%`);
                }
                break;

              case 'scan_error':
                console.log('❌ Received scan_error event');
                const errorMsg = messageData.error || 'An error occurred during scanning';
                setScanStatus('Scan failed');
                onError(errorMsg);
                break;

              default:
                console.log('Unknown message type:', messageData.type);
            }
          } else {
            console.log('Message without type field:', messageData);
          }
        }
      } catch (error) {
        console.error('Error handling message:', error);
      }
    };

    // Listen for messages from iframe
    window.addEventListener('message', handleMessage);

    // Handle visibility changes (app going to background)
    const handleVisibilityChange = () => {
      if (document.hidden && iframeRef.current?.contentWindow) {
        // Send blur event to pause camera
        iframeRef.current.contentWindow.postMessage(
          { type: 'blur' },
          '*'
        );
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('message', handleMessage);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [onScanComplete, onSkinScanComplete, onError]);

  const handleIframeLoad = () => {
    console.log('=== IFRAME LOADED ===');
    console.log('Iframe URL:', iframeUrl);
    if (iframeRef.current) {
      console.log('Iframe allow attribute:', iframeRef.current.getAttribute('allow'));
      console.log('Iframe sandbox attribute:', iframeRef.current.getAttribute('sandbox'));
    }
    setIsLoading(false);
    setScanStatus('Ready to scan');
  };

  const handleRetry = () => {
    setProgress(0);
    setScanStatus('Restarting...');
    setIsLoading(true);
    setIframeUrl('');
    if (requiresGestureForCamera) {
      setPermissionStatus('idle');
      setPermissionError(null);
    } else {
      initializeCamera('auto');
    }
  };

  return (
    <Card className="mb-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <Card.Title className="mb-0">Face Scan</Card.Title>
        <div>
          <Button variant="outline-secondary" size="sm" onClick={handleRetry} className="me-2">
            <KTSVG path='/media/icons/duotune/arrows/arr021.svg' className='svg-icon-3' />
          </Button>
          {onCancel && (
            <Button variant="outline-danger" size="sm" onClick={onCancel}>
              <KTSVG path='/media/icons/duotune/arrows/arr061.svg' className='svg-icon-3' />
            </Button>
          )}
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        <div style={{ 
          position: 'relative', 
          width: '100%', 
          height: '600px',
          minHeight: '400px',
          maxHeight: '90vh',
          backgroundColor: '#000', 
          borderRadius: '8px', 
          overflow: 'hidden' 
        }}>
          {(!iframeUrl || isLoading) && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(0,0,0,0.8)',
                zIndex: 10,
                padding: '1.5rem',
              }}
            >
              {!iframeUrl ? (
                <div className="text-center text-light" style={{ maxWidth: '420px' }}>
                  <h5 className="mb-3">Camera permission required</h5>
                  <p className="mb-4">
                    Please tap the button below and allow camera access to start the scan.
                  </p>
                  <Button
                    variant="light"
                    size="lg"
                    disabled={permissionStatus === 'requesting'}
                    onClick={() => initializeCamera('manual')}
                  >
                    {permissionStatus === 'requesting' ? 'Requesting...' : 'Enable Camera'}
                  </Button>
                  {permissionError && (
                    <Alert variant="danger" className="mt-3 mb-0">
                      {permissionError}
                    </Alert>
                  )}
                </div>
              ) : (
                <h5 className="text-light">Loading Face Scan Module...</h5>
              )}
            </div>
          )}
          
          <iframe
            ref={iframeRef}
            src={iframeUrl || 'about:blank'}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 'none',
            }}
            allow="camera; microphone; autoplay; fullscreen; screen-wake-lock; display-capture; geolocation"
            allowFullScreen={true}
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-downloads allow-storage-access-by-user-activation"
            onLoad={handleIframeLoad}
            title="Face Scan Module"
          />
        </div>

        <div className="mt-3 px-3">
          <p className="text-center text-muted mb-2">
            <strong>{scanStatus}</strong>
          </p>
          <ProgressBar now={progress} label={`${Math.round(progress)}%`} />
        </div>

        <div className="mt-3 px-3 pb-3">
          <p className="text-muted mb-2">
            <strong>Instructions:</strong>
          </p>
          <ul className="text-muted small">
            <li>Position your face in the center of the frame</li>
            <li>Ensure good lighting on your face</li>
            <li>Stay still and look at the camera</li>
            <li>The scan will take 30-60 seconds</li>
            <li>Allow camera permissions when prompted</li>
          </ul>
        </div>
      </Card.Body>
    </Card>
  );
};

export default FaceScanIframe;
